import { AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useLocation } from '#router'
import { Badge } from '@/components/ui/badge'
import { usePageTitle } from '@/hooks/use-document-title'
import { type RouterOutputs, trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import {
  formatLockLine,
  formatWeek,
  PHASE_LABEL,
  phaseOf,
  type SprintUiPhase,
} from './sprint-format'

type SprintSummary = RouterOutputs['admin']['sprints']['list'][number]
type StrandedRow = RouterOutputs['admin']['sprints']['listStranded'][number]

const PHASE_TONE: Record<SprintUiPhase, string> = {
  upcoming: 'text-muted-foreground',
  locking: 'text-foreground',
  running: 'text-[#51B0BC]',
  ended: 'text-muted-foreground',
}

/**
 * The Sprint calendar (LG-1004): every materialized week, who is in, and
 * which weeks still need a Homework placed before they lock.
 */
export function SprintsList() {
  usePageTitle('Sprints')
  const { navigate } = useLocation()
  const [view, setView] = useState<'active' | 'historical'>('active')
  const sprints = trpc.admin.sprints.list.useQuery()
  const stranded = trpc.admin.sprints.listStranded.useQuery()
  // Keyed to the data's freshness rather than mount: the list refetches on
  // focus, and the phase column has to move with it across a lock-in.
  // biome-ignore lint/correctness/useExhaustiveDependencies: dataUpdatedAt is the trigger, not an input
  const now = useMemo(() => new Date(), [sprints.dataUpdatedAt])

  const rows = useMemo(() => {
    const all = (sprints.data ?? []).map((row) => ({
      ...row,
      phase: phaseOf(now, row),
    }))
    return view === 'active'
      ? all.filter((row) => row.phase !== 'ended')
      : all.filter((row) => row.phase === 'ended').reverse()
  }, [sprints.data, view, now])

  // Red where the week is out of time: the Sprint running now, one locked
  // for Monday, and the next to lock. Later weeks are a heads-up in yellow.
  const nextUpcomingId = rows.find((row) => row.phase === 'upcoming')?.id
  const isUrgent = (row: (typeof rows)[number]) =>
    row.phase === 'running' ||
    row.phase === 'locking' ||
    row.id === nextUpcomingId

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Sprints</h1>
        <p className="text-muted-foreground text-sm">
          One week per row. Open a Sprint to add a client or grant a credit by
          hand.
        </p>
      </div>

      <StrandedNotice rows={stranded.data ?? []} />

      <div className="border-border mb-4 flex gap-4 border-b">
        {(['active', 'historical'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setView(tab)}
            className={cn(
              '-mb-px border-b-2 px-1 pb-2 text-sm font-medium capitalize',
              view === tab
                ? 'border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground border-transparent',
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {sprints.isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 py-10 text-sm">
          <RefreshCw size={16} className="animate-spin" /> Loading Sprints…
        </div>
      ) : sprints.error ? (
        <p className="text-destructive py-10 text-sm">
          Could not load Sprints: {sprints.error.message}
        </p>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground py-10 text-sm">
          {view === 'active'
            ? 'No Sprint is scheduled.'
            : 'Nothing has run yet.'}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left text-xs uppercase">
            <tr className="border-border border-b">
              <th className="py-2 pr-3 pl-3 font-medium">Week</th>
              <th className="py-2 pr-3 font-medium">Sprint</th>
              <th className="py-2 pr-3 font-medium">Lock-in</th>
              <th className="py-2 pr-3 text-right font-medium">Clients in</th>
              {view === 'active' && (
                <th className="py-2 pr-3 text-right font-medium">
                  Needs attention / Unfunded
                </th>
              )}
              <th className="py-2 pr-3 text-right font-medium">Clients out</th>
              <th className="w-6" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={(event) => {
                  // The name link handles a modified click natively (new
                  // tab); the row must not also navigate this one.
                  if (
                    event.defaultPrevented ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey ||
                    event.button !== 0
                  ) {
                    return
                  }
                  navigate(`/admin/sprints/${row.id}`)
                }}
                className={cn(
                  'border-border hover:bg-muted/40 cursor-pointer border-b',
                  row.phase === 'running' && 'border-l-2 border-l-[#51B0BC]',
                )}
              >
                <td className="py-3 pr-3 pl-3">
                  <div>{formatWeek(row.startsAt, row.endsAt)}</div>
                  <div className={cn('text-xs', PHASE_TONE[row.phase])}>
                    {PHASE_LABEL[row.phase]}
                  </div>
                </td>
                <td className="py-3 pr-3">
                  <Link
                    href={`/admin/sprints/${row.id}`}
                    className="font-medium hover:underline"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {row.name}
                  </Link>
                  <SprintBadges sprint={row} />
                </td>
                <td className="text-muted-foreground py-3 pr-3">
                  {formatLockLine(now, row)}
                </td>
                <td className="py-3 pr-3 text-right tabular-nums">
                  {row.inSprint}
                </td>
                {view === 'active' && (
                  <td className="py-3 pr-3 text-right tabular-nums">
                    <UnplannedCell
                      count={row.unplanned}
                      urgent={isUrgent(row)}
                    />
                    <span className="text-muted-foreground"> / </span>
                    <span
                      className="text-muted-foreground"
                      title="Clients whose balance will not reach this week at lock-in"
                    >
                      {row.unfundedAhead === 0 ? '—' : row.unfundedAhead}
                    </span>
                  </td>
                )}
                <td className="text-muted-foreground py-3 pr-3 text-right tabular-nums">
                  {row.outOfSprint}
                </td>
                <td className="text-muted-foreground py-3">
                  <ChevronRight size={16} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function SprintBadges({
  sprint,
}: {
  sprint: Pick<SprintSummary, 'status' | 'isFree'>
}) {
  if (sprint.status !== 'suspended' && !sprint.isFree) return null
  return (
    <span className="ml-2 inline-flex gap-1 align-middle">
      {sprint.status === 'suspended' && (
        <Badge variant="outline">Suspended</Badge>
      )}
      {sprint.isFree && <Badge variant="secondary">Free week</Badge>}
    </span>
  )
}

/**
 * Clients in the Sprint with no Homework placed. Unfunded clients are not
 * counted: they need nothing from this page (a standing alert for them is
 * a later build).
 */
function UnplannedCell({ count, urgent }: { count: number; urgent: boolean }) {
  if (count === 0) {
    return <span className="text-muted-foreground">—</span>
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium',
        urgent ? 'text-destructive' : 'text-yellow-500',
      )}
    >
      <AlertTriangle size={14} /> {count}
      <span className="sr-only">
        {count === 1 ? 'client' : 'clients'} with no Homework placed for the
        week
      </span>
    </span>
  )
}

/**
 * `upcoming` rows on ended Sprints. No sweep touches them and neither does
 * this page; it only makes sure they are seen. A write-off, if one is ever
 * built, belongs with suspend (LG-1078).
 */
function StrandedNotice({ rows }: { rows: StrandedRow[] }) {
  if (rows.length === 0) return null
  return (
    <div className="border-destructive/40 bg-destructive/5 mb-6 rounded-lg border p-4 text-sm">
      <div className="text-destructive flex items-center gap-2 font-medium">
        <AlertTriangle size={16} />
        {rows.length === 1
          ? 'One participation was never settled'
          : `${rows.length} participations were never settled`}
      </div>
      <p className="text-muted-foreground mt-1">
        These clients are still marked upcoming on a Sprint that has ended: no
        lock sweep succeeded during its window, or it was suspended throughout.
        They were never charged. There is no write-off on this page; LG-1078
        decides what suspend does with them.
      </p>
      <ul className="mt-2 space-y-1">
        {rows.map((row) => (
          <li key={row.participationId} className="flex flex-wrap gap-x-2">
            <Link
              href={`/admin/coaching/client/${row.clientId}`}
              className="font-medium hover:underline"
            >
              {row.clientName}
            </Link>
            <span className="text-muted-foreground">in</span>
            <Link
              href={`/admin/sprints/${row.sprintId}`}
              className="hover:underline"
            >
              {row.sprintName}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
